import { memo } from "react";
import {
  Container,
  FooterContentWrapper,
  HeaderFloatingButtonWrapper,
  HeaderFooter,
  HeaderWrapper,
  ImageWrapper,
  RotatedDivider,
  UserImage,
} from "./styles";
import Shape from "../Shape";
import { Box } from "@mui/material";
import Spacer from "../Spacer";
import IconButton from "../IconButton";
import Text from "../Text";
import Theme from "@/utils/theme";
import { useNavigate } from "react-router-dom";
import PointsSlider from "@/components/PointsSlider";
import avatar from "/assets/avatar.png";
import { useStages } from "@/context/StagesContext";
import CircleCheck from "@/ui/icons/CircleCheck";
import InfoIcon from "@/ui/icons/InfoIcon";
import ResaleCodeModal from "@/components/UserInfoHeader/components/ResaleCodeModal";
import { realityInfo } from "@/utils/realityInfo";
import ResaleCodeButton from "./components/ResaleCodeButton";
import { useUserInfoHeader } from "./useUserInfoHeader";
import { getAgeFromBirthDate } from "@/helpers/date.helper";
import { Tooltip } from "../Tooltip";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ENABLE_POINTS_UI } from "@/utils/feature-flags";
import MeuProgressoSection from "./components/HourlyProgressPlaceholder";

const UserInfoHeader = () => {
  const navigate = useNavigate();
  const { isValidStage } = useStages();
  const {
    userInfo,
    isVisibleSaleCode,
    toggleIsVisibleSaleCode,
    points,
    storeName,
    isVisibleResaleCodeTooltip,
    toggleIsVisibleResaleCodeTooltip,
  } = useUserInfoHeader();
  const { isMobile } = useIsMobile();

  return (
    <Container>
      <HeaderWrapper $hasBottomBorderRadius={!realityInfo.showResaleCode}>
        <Shape
          leftNode={
            <ImageWrapper>
              <UserImage src={userInfo?.imageUrl || avatar} />
            </ImageWrapper>
          }
        >
          <Spacer width="1rem" />
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            flex={1}
          >
            <Text fontSize="1.5rem">Olá, {userInfo?.name}!</Text>
            {userInfo?.birthDate && userInfo?.city && (
              <Text color={Theme.colors.text.secondary} mt="0.5rem">
                {getAgeFromBirthDate(userInfo.birthDate)} anos | {userInfo.city}
              </Text>
            )}

            <HeaderFloatingButtonWrapper>
              <IconButton
                icon="settings"
                onClick={() => navigate("/profile")}
              />
            </HeaderFloatingButtonWrapper>
          </Box>
        </Shape>

        <>
          <Box display={{ xs: "none", sm: "flex" }}>
            <RotatedDivider
              margin="0rem 1rem"
              vertical
              color={Theme.colors.neutral.mid_dark}
            />
          </Box>
          <Box display={{ xs: "flex", sm: "none" }}>
            <Spacer height="0.5rem" />
          </Box>

          <Shape>
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              width="100%"
              justifyContent="center"
              px={{ xs: "1rem", sm: 0 }}
            >
              <MeuProgressoSection isValidStage={isValidStage} />

              {ENABLE_POINTS_UI && (
                <>
                  <Spacer height="1.5rem" />
                  <Text color={Theme.colors.text.secondary}>Meus Pontos</Text>
                  <Spacer height="1rem" />
                  <PointsSlider
                    currentPoints={points?.earnedPoints || 0}
                    totalPoints={points?.maximumPoints || 0}
                  />
                </>
              )}
            </Box>
          </Shape>
        </>
      </HeaderWrapper>

      {realityInfo.showResaleCode && (
        <HeaderFooter>
          <FooterContentWrapper $hasCode={!!userInfo?.resaleCode}>
            {!!userInfo?.resaleCode && <CircleCheck size={24} />}

            <Text fontSize="0.85rem" fontWeight={400} mx="0.4rem">
              Código da Revenda:
            </Text>

            {userInfo?.resaleCode ? (
              <Text
                color={Theme.colors.feedback.positive}
                fontSize="0.85rem"
                fontWeight={500}
              >
                {userInfo.resaleCode}
              </Text>
            ) : (
              <ResaleCodeButton onClick={toggleIsVisibleSaleCode} />
            )}

            <Tooltip
              isOpen={isVisibleResaleCodeTooltip}
              onClose={toggleIsVisibleResaleCodeTooltip}
              placement={isMobile ? "bottom-end" : "bottom-start"}
              title="Este campo não é obrigatório. O Código da Revenda relaciona o seu perfil com uma Loja de Materiais para Construção. Entre em contato com a revenda Quartzolit mais próxima e solicite este código gratuito."
            >
              <Box
                display="flex"
                style={{ cursor: "pointer" }}
                p="0.25rem"
                onClick={toggleIsVisibleResaleCodeTooltip}
              >
                <InfoIcon size={20} />
              </Box>
            </Tooltip>

            {!!userInfo?.resaleCode && (
              <Box display={{ xs: "none", sm: "flex" }}>
                <Text color={Theme.colors.neutral.light}> | </Text>

                <Text>Você Está Representando a Revenda: </Text>
                <Text color={Theme.colors.feedback.positive}>{storeName}</Text>
              </Box>
            )}
          </FooterContentWrapper>
        </HeaderFooter>
      )}

      <ResaleCodeModal
        isOpen={isVisibleSaleCode}
        onClose={toggleIsVisibleSaleCode}
      />
    </Container>
  );
};

export default memo(UserInfoHeader);
